import React, { useEffect, useState } from "react";
import RomboLogo from "../../assets/images/logo-map.png";
import LoaderRombo from "./LoaderRombo";
import i18n from "../../i18n";
import genericMap from "../../assets/images/generic-map.jpg";

const GpxViewer = ({ gpxData, focusPoint }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [minimumLoading, setMinimumLoading] = useState(true);

  const getViewerBaseUrl = () => {
    const env = import.meta.env.VITE_APP_DEFAULTAUTH;
    return env === 'dev' 
      ? 'https://gpx.viewer.stg.rombo.mx' 
      : 'https://gpx.viewer.rombo.mx';
  };

  const viewerBaseUrl = getViewerBaseUrl();

  useEffect(() => {
    const timer = setTimeout(() => {
      setMinimumLoading(false); 
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!gpxData) {
      setLoading(false);
      return;
    }

    const verifyGpxUrl = async () => {
      try {
        setLoading(true);
        const response = await fetch(gpxData, { method: "HEAD" });
        if (!response.ok) {
          throw new Error("GPX file not accessible");
        }
      } catch (err) {
        console.error("Error verifying GPX URL:", err);
        setError("Error loading map. Please reload the page.");
      } finally {
        setLoading(false);
      }
    };

    verifyGpxUrl();
  }, [gpxData]);

  if (loading || minimumLoading)
    return (
      <div className="gpx-viewer-loader-container">
        <LoaderRombo />
      </div>
    );

  const renderErrorState = (iconClass, title, message) => (
    <div 
      className="gpx-viewer-error-container"
      style={{
        backgroundImage: `url(${genericMap})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="error-overlay">
        <div className="error-content text-center">
          <div className="error-icon mb-3">
            <i className={`bx ${iconClass} bx-burst bx-lg`} />
          </div>
          <h5 className="error-title mb-2">{title}</h5>
          <p className="error-message mb-3">{message}</p>
          <button
            className="btn btn-danger btn-sm shadow-sm"
            onClick={() => window.location.reload()}
          >
            {i18n.t("ReloadMap")}
          </button>
        </div>
      </div>
    </div>
  );

  if (error)
    return renderErrorState(
      "bx-error-circle", 
      i18n.t("LoadingMapError"), 
      error ?? i18n.t("UnexpectedError")
    );

  if (!gpxData)
    return renderErrorState(
      "bx-map", 
      i18n.t("LoadingMapError"), 
      i18n.t("NoRouteDataAvailable")
    );

  const options = {
    token: import.meta.env.VITE_MAPBOX_TOKEN,
    files: [gpxData],
    basemap: "mapbox-outdoors",
    units: "metric",
    theme: "light",
    controls: {
      speed: true,
      distanceMarkers: true,
      openInGpxStudio: false,
      layers: false,
      info: false,
      elevationProfile: false,
      summary: false 
    },
    directionMarkers: false,
    follow: true,
    noedit: true,
    nosave: true,
    elevation: false,
    showElevation: false,
  };

  const encodedOptions = encodeURIComponent(JSON.stringify(options));

  // Centrado en un punto (origen/destino/escala): el viewer (gpx.studio) usa
  // MapLibre con hash de URL. Cuando la URL trae un hash `#zoom/lat/lng`, el
  // mapa abre centrado en ese punto y NO hace el auto-fit a la ruta completa
  // (ver bounds.ts: solo ajusta si el hash está vacío). Sin focusPoint, se
  // mantiene el comportamiento por defecto (encuadra toda la ruta).
  const hasFocus =
    focusPoint &&
    Number.isFinite(focusPoint.lat) &&
    Number.isFinite(focusPoint.lng);
  const zoom = hasFocus ? focusPoint.zoom ?? 15 : null;
  const mapHash = hasFocus
    ? `#${zoom}/${focusPoint.lat}/${focusPoint.lng}`
    : "";
  const iframeSrc = `${viewerBaseUrl}/embed?options=${encodedOptions}${mapHash}`;

  return (
    <div style={{ 
      height: "100%", 
      position: "relative",
      width: "100%",
      minWidth: "450px"
    }}>
      <iframe
        title="GPX Viewer"
        src={iframeSrc}
        width="100%"
        height="100%"
        style={{ 
          border: "none", 
          outline: "none",
          minWidth: "450px"
        }}
        loading="lazy"
        allow="geolocation"
        allowFullScreen
      />
      <img
        src={RomboLogo}
        alt="Rombo Logo"
        className="rombo-map-logo"
      />
    </div>
  );
};

export default GpxViewer;
